import TradeInterface from 'app/interfaces/TradeInterface';
import {toastError, toastSuccess} from 'app/components/toasts/liteToast';
import {modalConfirm} from 'app/utils/modals/popups';
import Get from 'app/utils/Get';
import Post from 'app/utils/Post';
import I18n from 'app/i18n';
import {action, makeObservable, observable} from 'mobx';

export type TradeType = 'sent' | 'received' | 'history';

export const TAB_CONFIG = {
    history: {
        empty_subtitle_key: 'empty_history_offers',
        type: 'history' as TradeType
    },
    incoming: {
        empty_subtitle_key: 'empty_received_offers',
        type: 'received' as TradeType
    },
    outgoing: {
        empty_subtitle_key: 'empty_sent_offers',
        type: 'sent' as TradeType
    }
};

export type TradeTabKey = keyof typeof TAB_CONFIG;

export const getValidTabKey = (type: string | null): TradeTabKey => {
    if (type && type in TAB_CONFIG) {
        return type as TradeTabKey;
    }

    return 'outgoing';
};

export interface TabState {
    hasMore: boolean;
    loaded: boolean;
    page: number;
    trades: TradeInterface[];
}

interface MyTradesResponse {
    data: TradeInterface[];
    has_more: boolean;
    total_incoming: number;
    total_outgoing: number;
}

interface TradeActionResponse {
    message: string;
}

interface TradeActionErrorResponse {
    message?: string;
    [key: string]: string | string[] | undefined;
}

export const createEmptyTabState = (): TabState => ({
    hasMore: false,
    loaded: false,
    page: 1,
    trades: []
});

export class MyTradesStore {
    @observable cache: Partial<Record<TradeType, TabState>> = {};

    @observable contentLoading: boolean = false;

    @observable total_incoming: number | null = null;

    @observable total_outgoing: number | null = null;

    constructor() {
        makeObservable(this);
    }

    getTabState(type: TradeType): TabState {
        return this.cache[type] || createEmptyTabState();
    }

    @action
    ensureTabData = (type: TradeType): void => {
        const tabState = this.getTabState(type);

        if (tabState.loaded) {
            return;
        }

        this.setContentLoading(true);
        this.loadTrades(type);
    };

    @action
    reloadCurrentTab = (type: TradeType): void => {
        this.resetTab(type);
        this.setContentLoading(true);
        this.loadTrades(type);
    };

    loadTrades = (type: TradeType): Promise<void> => {
        const {page} = this.getTabState(type);

        return new Get({
            params: {
                page,
                type
            },
            url: '/trades/my_trades'
        }).execute()
            .then((fetchResponse: Response) => {
                if (fetchResponse.ok) {
                    return fetchResponse.json().then((jsonResponse: MyTradesResponse) => {
                        this.updateTabState(type, {
                            hasMore: jsonResponse.has_more,
                            loaded: true,
                            page: page + 1,
                            trades: jsonResponse.data
                        });
                        this.setContentLoading(false);
                        this.setTotals(jsonResponse.total_incoming, jsonResponse.total_outgoing);
                    });
                }

                return fetchResponse.json().then((jsonResponse: TradeActionResponse) => {
                    this.setContentLoading(false);
                    toastError(jsonResponse.message);
                });
            });
    };

    acceptTrade = (id: number, type: TradeType): void => {
        modalConfirm(I18n.t('accept_trade_confirm'), () => {
            this.executeTradeAction(id, type, 'accept');
        });
    };

    cancelTrade = (id: number, type: TradeType): void => {
        modalConfirm(I18n.t('cancel_trade_confirm'), () => {
            this.executeTradeAction(id, type, 'cancel');
        });
    };

    @action
    setContentLoading(contentLoading: boolean): void {
        this.contentLoading = contentLoading;
    }

    @action
    setTotals(totalIncoming: number | null, totalOutgoing: number | null): void {
        this.total_incoming = totalIncoming;
        this.total_outgoing = totalOutgoing;
    }

    @action
    updateTabState(type: TradeType, tabState: TabState): void {
        this.cache = {
            ...this.cache,
            [type]: tabState
        };
    }

    @action
    resetTab(type: TradeType): void {
        this.cache = {
            ...this.cache,
            [type]: createEmptyTabState()
        };
    }

    @action
    reset(): void {
        this.cache = {};
        this.setContentLoading(false);
        this.setTotals(null, null);
    }

    private executeTradeAction = (id: number, type: TradeType, tradeAction: 'accept' | 'cancel'): void => {
        new Post({
            params: {
                type
            },
            url: `/trades/${id}/${tradeAction}`
        })
            .execute()
            .then((fetchResponse: Response) => {
                if (fetchResponse.ok) {
                    return fetchResponse.json().then((jsonResponse: TradeActionResponse) => {
                        this.reloadCurrentTab(type);
                        toastSuccess(jsonResponse.message);
                    });
                }

                return fetchResponse.json().then((jsonResponse: TradeActionErrorResponse) => {
                    toastError(this.extractErrorMessage(jsonResponse));
                }).catch(() => {
                    toastError(I18n.t('server_error_try_later'));
                });
            })
            .catch(() => {
                toastError(I18n.t('server_error_try_later'));
            });
    };

    private extractErrorMessage = (jsonResponse: TradeActionErrorResponse): string => {
        if (typeof jsonResponse.message === 'string' && jsonResponse.message.length > 0) {
            return jsonResponse.message;
        }

        const firstError = Object.values(jsonResponse).find(
            (value): value is string[] => Array.isArray(value) && typeof value[0] === 'string'
        );

        return firstError?.[0] || I18n.t('server_error_try_later');
    };
}
